import * as React from 'react'
import { useEffect, useRef } from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Clinic } from '@magnetar/admin-api-interfaces'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onSearch?: (value: string) => void
  clinics?: Clinic[]
  selectedClinicId?: number
  onClinicChange?: (clinicId: number | undefined) => void
  meta?: {
    reloadData: () => void
  }
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onSearch,
  clinics,
  selectedClinicId,
  onClinicChange,
  meta,
}: DataTableProps<TData, TValue>) {
  const [searchValue, setSearchValue] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'clinic_user_id', desc: false },
  ])
  const onSearchRef = useRef(onSearch)
  onSearchRef.current = onSearch
  const isFirstRender = useRef(true)

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    meta,
  })

  useEffect(() => {
    // 跳過首次 render，避免不必要的 API 呼叫
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    const timer = setTimeout(() => {
      onSearchRef.current?.(searchValue)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchValue])

  return (
    <div>
      <div className="flex items-center gap-4 py-4">
        {clinics && clinics.length > 0 && (
          <Select
            value={selectedClinicId?.toString() ?? 'all'}
            onValueChange={(value) => {
              onClinicChange?.(value === 'all' ? undefined : parseInt(value, 10))
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="選擇診所" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部診所</SelectItem>
              {clinics.map((clinic) => (
                <SelectItem key={clinic.clinic_id} value={clinic.clinic_id.toString()}>
                  {clinic.clinic_name}
                  {clinic.alias ? ` (${clinic.alias})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Input
          placeholder="搜尋帳號或姓名..."
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          className="max-w-sm"
        />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  沒有資料
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
